"use client";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { useSession } from "@/context/SessionContext";
import { useTourButtons } from "@/context/TourButtonContext";
import { TourDefinition, useTour } from "@/context/TourContext";
import { updateShowTourButtons } from "@/utils/serverActions/settings/settingProfileAction";
import React, { useEffect, useState } from "react";
import { LuPlay, LuSettings } from "react-icons/lu";

interface TourMenuProps {
  children: React.ReactNode;
}

export const TourMenu: React.FC<TourMenuProps> = ({ children }) => {
  const { getAvailableTours, getTourProgress, startTour } = useTour();
  const { showTourButtons, setShowTourButtons } = useTourButtons();
  const { session, refreshSession } = useSession();
  const [tours, setTours] = useState<TourDefinition[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    const loadTours = async () => {
      const availableTours = await getAvailableTours();
      setTours(availableTours);
    };
    loadTours();
  }, [getAvailableTours]);

  const handleStartTour = async (tourId: string) => {
    await startTour(tourId);
    setIsOpen(false);
  };

  const handleToggleTourButtons = async (enabled: boolean) => {
    if (isUpdating || !session.profileId) return;

    setIsUpdating(true);
    try {
      // Update in database
      await updateShowTourButtons(session.profileId, enabled);

      // Update local state
      setShowTourButtons(enabled);

      // Update localStorage as cache
      localStorage.setItem("show-tour-buttons", JSON.stringify(enabled));

      // Refresh session to get updated value from database
      await refreshSession();
    } catch (error) {
      console.error("Error updating show tour buttons preference:", error);
      // Revert on error
      setShowTourButtons(!enabled);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-w-4xl" closeButton>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LuPlay className="h-5 w-5" />
            Todos los Tours
          </DialogTitle>
          <DialogDescription className="text-left">
            Explora todas las funciones de Orquesti con nuestros tours guiados.
            Cada tour te enseñará cómo usar una sección específica de la
            aplicación.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 max-h-[60dvh] overflow-y-auto">
          {/* Tour Controls */}
          <div className="flex items-center justify-between p-2 sm:p-4 border rounded-lg bg-gray-50">
            <div className="flex items-center gap-3">
              <LuSettings className="h-5 w-5 text-gray-600" />
              <div>
                <h3 className="text-sm font-medium text-gray-900">
                  Mostrar botones de tour en la barra de navegación
                </h3>
                <p className="text-xs text-gray-500">
                  Puedes reactivar esta opción en la sección Ajustes → Perfil
                </p>
              </div>
            </div>
            <Switch
              checked={showTourButtons}
              onCheckedChange={handleToggleTourButtons}
              disabled={isUpdating}
            />
          </div>

          {/* Tours List */}
          <div className="grid gap-3 ">
            {tours.map((tour) => {
              const isCompleted = getTourProgress(tour.id);
              return (
                <div
                  key={tour.id}
                  className="flex items-center justify-between  p-2 sm:p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{tour.icon}</span>
                    <div>
                      <h5 className="text-base font-medium">{tour.name}</h5>
                      <p className="text-sm text-gray-600">
                        {tour.description}
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={() => handleStartTour(tour.id)}
                    variant={isCompleted ? "outline" : "default"}
                    size="sm"
                  >
                    <LuPlay className="h-4 w-4 mr-1" />
                    {isCompleted ? "Repetir" : "Iniciar"}
                  </Button>
                </div>
              );
            })}
          </div>
        </div>

        {/* <div className="flex justify-end pt-4 border-t">
          <Button variant="outline" onClick={() => setIsOpen(false)}>
            Cerrar
          </Button>
        </div> */}
      </DialogContent>
    </Dialog>
  );
};
